import React, { Component } from "react";
import { PropTypes } from "prop-types";
import {
  Button,
  TooltipProvider,
  Link,
} from "@zendesk/self-service-components";
import { Spinner } from "@zendeskgarden/react-loaders";
import { Prompt } from "react-router-dom";
import Chrome from "@zendesk/knowledge-chrome/lib/Chrome";
import t from "@zendesk/client-i18n-tools";
import classNames from "classnames";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import isEqual from "lodash/isEqual";
import { getCurrentBrandId, hasFeature } from "@zendesk/knowledge-context";
import { STAFF, SIGNED_IN_USERS } from "../../constants";
import { getUserSegmentsUrl } from "utils/urls";
import * as actions from "../../actions/userSegment";
import Notifications from "../Notifications";
import Title from "./components/Title";
import AutocompleteList from "../AutocompleteList";
import UserList from "./components/UserList";
import styles from "./index.css";

export class UserSegment extends Component {
  constructor(props) {
    super(props);

    this.onNameChange = this.onNameChange.bind(this);
    this.onSave = this.onSave.bind(this);
    this.onBeforeUnload = this.onBeforeUnload.bind(this);
  }

  componentWillMount() {
    const { fetchUserSegment, userSegmentId } = this.props;
    if (userSegmentId) {
      fetchUserSegment(userSegmentId);
    }
  }

  componentDidMount() {
    window.addEventListener("beforeunload", this.onBeforeUnload);
  }

  componentWillUnmount() {
    const { resetUserSegment } = this.props;

    resetUserSegment();
    window.removeEventListener("beforeunload", this.onBeforeUnload);
  }

  onBeforeUnload(e) {
    const { hasChanges } = this.props;

    if (hasChanges) {
      const message = t(
        "knowledge-user-segments.userSegment.unsavedLeaveConfirmation"
      );

      e.returnValue = message;
      return message;
    }
  }

  onNameChange(newName) {
    const { changeName } = this.props;
    changeName(newName);
  }

  onSave() {
    const { saveUserSegment, userSegmentId } = this.props;
    saveUserSegment(userSegmentId);
  }

  renderAppliesToLink() {
    const { hasArticleViewPermissions, userSegmentId } = this.props;

    if (!hasArticleViewPermissions) {
      return null;
    }

    return (
      <p className={styles.description}>
        {t
          .parts("knowledge-user-segments.userSegment.appliesTo", {
            articlesLink: (
              <Link
                key="applies-to-articles-link"
                href={`/knowledge/lists?query=is_source%3Atrue+user_segment_id%3A${userSegmentId}&brand_id=${getCurrentBrandId()}`}
                target="_blank"
              >
                {t("knowledge-user-segments.userSegment.theseArticles")}
              </Link>
            ),
          })
          .map((part, i) => {
            switch (part.type) {
              case "text":
              case "variable":
                return part.value;
              case "newline":
                return <br key={`newline-${i}`} />;
            }
          })}
      </p>
    );
  }

  renderRestrictions() {
    const {
      builtIn,
      userType,
      addTag,
      addOrganization,
      addGroup,
      isFetchingOrganizations,
      isFetchingOrganizationsForbidden,
      isFetchingGroups,
      tags,
      organizations,
      groups,
    } = this.props;

    if (builtIn) {
      return (
        <div className={styles.userSegmentContent}>
          <p className={styles.description}>
            {t("knowledge-user-segments.userSegment.builtInDescription")}
          </p>

          {this.renderAppliesToLink()}
        </div>
      );
    }

    return (
      <div className={styles.userSegmentContent}>
        {this.renderAppliesToLink()}

        <h2 className={styles.resrtrictionsTitle}>
          {t("knowledge-user-segments.userSegment.matchTheFollowing")}
        </h2>

        <AutocompleteList
          isLoading={false}
          items={tags}
          type="tags"
          newText={t("knowledge-user-segments.userSegment.addTag")}
          conjunction={t("knowledge-user-segments.userSegment.conjunctionAnd")}
          onAddNew={addTag}
          title={t("knowledge-user-segments.userSegment.tags")}
          description={t(
            "knowledge-user-segments.userSegment.tagsDescription",
            {
              learnMoreUrl: t(
                "knowledge-user-segments.userSegment.tagsDescriptionLearnMoreUrl"
              ),
            }
          )}
        />

        {userType === SIGNED_IN_USERS && (
          <AutocompleteList
            isLoading={isFetchingOrganizations}
            items={organizations}
            type="organizations"
            newText={t("knowledge-user-segments.userSegment.addOrganization")}
            conjunction={t("knowledge-user-segments.userSegment.conjunctionOr")}
            onAddNew={addOrganization}
            title={t("knowledge-user-segments.userSegment.organizations")}
            error={
              isFetchingOrganizationsForbidden
                ? t(
                    "knowledge-user-segments.userSegment.organizationsForbidden"
                  )
                : null
            }
            description={t(
              "knowledge-user-segments.userSegment.organizationsDescription",
              {
                learnMoreUrl: t(
                  "knowledge-user-segments.userSegment.organizationsDescriptionLearnMoreUrl"
                ),
              }
            )}
          />
        )}

        {userType === STAFF && (
          <AutocompleteList
            isLoading={isFetchingGroups}
            items={groups}
            type="groups"
            newText={t("knowledge-user-segments.userSegment.addGroup")}
            conjunction={t("knowledge-user-segments.userSegment.conjunctionOr")}
            onAddNew={addGroup}
            title={t("knowledge-user-segments.userSegment.groups")}
            description={t(
              "knowledge-user-segments.userSegment.groupsDescription",
              {
                learnMoreUrl: t(
                  "knowledge-user-segments.userSegment.groupsDescriptionLearnMoreUrl"
                ),
              }
            )}
          />
        )}
      </div>
    );
  }

  render() {
    const { name, builtIn, hasChanges, userType, isLoaded } = this.props;

    const isNameEmpty = name.length === 0;

    const actionButton = !builtIn && (
      <Button
        type="primary"
        size="medium"
        testId="save-button"
        disabled={!hasChanges || isNameEmpty}
        onClick={this.onSave}
      >
        {t("knowledge-user-segments.userSegment.save")}
      </Button>
    );

    return (
      <Chrome
        brandId={getCurrentBrandId()}
        dir={t.dir}
        topNav={
          <Chrome.Breadcrumbs>
            <Chrome.Breadcrumbs.Item>
              <a href={getUserSegmentsUrl()}>
                {t("knowledge-user-segments.userPermissions")}
              </a>
            </Chrome.Breadcrumbs.Item>
            <Chrome.Breadcrumbs.Separator />
            <Chrome.Breadcrumbs.Item>
              <a href={getUserSegmentsUrl()}>
                {t("knowledge-user-segments.name")}
              </a>
            </Chrome.Breadcrumbs.Item>
            <Chrome.Breadcrumbs.Separator />
            <Chrome.Breadcrumbs.Item>{name}</Chrome.Breadcrumbs.Item>
          </Chrome.Breadcrumbs>
        }
      >
        <div className={styles.contentWrapper}>
          <Notifications />
          <TooltipProvider>
            <div className={styles.primaryContent}>
              <div className={styles.contentView}>
                <div>
                  {!isLoaded ? (
                    <div className={styles.userSegmentContent}>
                      <div className={styles.loader}>
                        <Spinner />
                      </div>
                    </div>
                  ) : (
                    <div className={styles.content}>
                      <Title
                        builtIn={builtIn}
                        userType={userType}
                        name={name}
                        actionButton={actionButton}
                        onChange={this.onNameChange}
                      />

                      {this.renderRestrictions()}
                    </div>
                  )}
                </div>

                <Prompt
                  when={hasChanges}
                  message={t(
                    "knowledge-user-segments.userSegment.unsavedLeaveConfirmation"
                  )}
                />
              </div>
            </div>

            <div className={styles.secondaryContentWrapper}>
              <div
                className={classNames(
                  styles.contentView,
                  styles.userSegmentContent
                )}
              >
                <UserList />
              </div>
            </div>
          </TooltipProvider>
        </div>
      </Chrome>
    );
  }
}

UserSegment.propTypes = {
  userSegmentId: PropTypes.string,
  name: PropTypes.string,
  builtIn: PropTypes.bool,
  hasChanges: PropTypes.bool,
  hasArticleViewPermissions: PropTypes.bool,
  isLoaded: PropTypes.bool.isRequired,
  isFetchingOrganizations: PropTypes.bool,
  isFetchingOrganizationsForbidden: PropTypes.bool,
  isFetchingGroups: PropTypes.bool,
  fetchUserSegment: PropTypes.func,
  addTag: PropTypes.func,
  addGroup: PropTypes.func,
  addOrganization: PropTypes.func,
  resetUserSegment: PropTypes.func,
  changeName: PropTypes.func,
  saveUserSegment: PropTypes.func,
  userType: PropTypes.oneOf([STAFF, SIGNED_IN_USERS]),
  tags: PropTypes.arrayOf(PropTypes.string),
  organizations: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
    })
  ),
  groups: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
    })
  ),
};

UserSegment.defaultProps = {
  hasChanges: false,
  hasArticleViewPermissions: false,
  name: "",
  builtIn: false,
  isFetchingOrganizations: false,
  isFetchingOrganizationsForbidden: false,
  isFetchingGroups: false,
  isLoaded: false,
};

const mapStateToProps = ({ userSegment }, ownProps) => ({
  userSegmentId: ownProps.match.params.id,
  name: userSegment.name,
  builtIn: userSegment.builtIn,
  userType: userSegment.userType,
  tags: userSegment.tags,
  organizations: userSegment.organizations,
  groups: userSegment.groups,
  users: userSegment.users,
  isLoaded: userSegment.isLoaded,
  isFetchingOrganizations: userSegment.isFetchingOrganizations,
  isFetchingOrganizationsForbidden:
    userSegment.isFetchingOrganizationsForbidden,
  isFetchingGroups: userSegment.isFetchingGroups,
  hasArticleViewPermissions: hasFeature("guide_article_view_permissions"),
  hasChanges:
    !isEqual(userSegment.savedTags, userSegment.tags) ||
    !isEqual(userSegment.savedOrganizations, userSegment.organizations) ||
    !isEqual(userSegment.savedGroups, userSegment.groups) ||
    userSegment.savedName !== userSegment.name,
});

const mapDispatchToProps = dispatch =>
  bindActionCreators({ ...actions }, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(UserSegment);



// WEBPACK FOOTER //
// ./src/components/UserSegment/index.js